import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import type { ApplyV2SettingsSectionName, NavItemsState } from 'core/app/types/applyV2Type'
import React, { type FC, type ReactNode, useEffect, useRef } from 'react'
import clsx from 'clsx'
import { Tooltip } from '@chakra-ui/react'
import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import './ApplyNavBtn.scss'

const baseClass = 'ApplyNavBtn'

interface ApplyNavBtnProps {
    onClick?: () => void
    className?: string
    title?: string
    children?: ReactNode
    state?: NavItemsState
    isLoading?: boolean
    tooltipLabel?: string
    name?: ApplyV2SettingsSectionName
}

const ApplyNavBtn: FC<ApplyNavBtnProps> = ({
    onClick = () => {},
    title = '',
    state = 'default',
    className,
    children,
    isLoading = false,
    tooltipLabel = '',
    name,
}) => {
    // const { isFinalStep, setIsFinalStep } = useApplyV2Context()
    const buttonRef = useRef<HTMLButtonElement>(null)
    const { getTitle } = useApplyV2NavTranslations()

    useEffect(() => {
        if (state === 'active' && buttonRef.current) {
            const timer = setTimeout(() => {
                buttonRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    // block: 'nearest',
                    // inline: 'start',
                })
            }, 100)

            return () => clearTimeout(timer)
        }
    }, [state])

    return (
        <Tooltip
            hasArrow
            isDisabled={isLoading || state === 'disabled' || state === 'active'}
            openDelay={500}
            label={tooltipLabel}
            // placement={'right'}
            padding={'8px 16px'}
            borderRadius={'4px'}
            backgroundColor={'#19191A'}
            fontWeight={600}
            fontSize={'12px'}
        >
            <button
                ref={buttonRef}
                onClick={() => {
                    // console.log('nab btn', isFinalStep)
                    // if (isFinalStep) {
                    //     console.log('nab btn hh', isFinalStep)
                    //     setIsFinalStep(false)
                    // }
                    if (state === 'active') return
                    onClick()
                }}
                className={clsx(baseClass, className, {
                    [`${baseClass}--${state}`]: state,
                    [`${className}--${state}`]: !!className,
                    [`${baseClass}--loading`]: isLoading,
                    [`${className}--loading`]: !!className && isLoading,

                    [`${baseClass}--parsing`]: name === 'cv' && state === 'parsing',
                })}
                disabled={isLoading || state === 'disabled'}
            >
                <div
                    className={clsx(`${baseClass}__wrap`, {
                        [`${className}__wrap`]: !!className,
                        [`${baseClass}__wrap--${state}`]: state,
                        [`${className}__wrap--${state}`]: !!className && state,
                        [`${baseClass}__wrap--loading`]: isLoading,
                        [`${className}__wrap--loading`]: !!className && isLoading,
                    })}
                >
                    <ApplySkeleton isLoading={isLoading}>
                        <div
                            className={clsx(`${baseClass}__icon`, {
                                [`${className}__icon`]: !!className,
                                [`${className}__icon--${state}`]: !!className && state,
                                [`${baseClass}__icon--loading`]: isLoading,
                                [`${className}__icon--loading`]: !!className && isLoading,
                            })}
                        >
                            {name === 'cv' && state === 'parsing' && (
                                <div className={`${baseClass}__icon--loader-parsing`}></div>
                            )}
                            {/*{name === 'cv' && <div className={`${baseClass}__icon--loader-parsing`}></div>}*/}
                            {(state === 'default' || state === 'disabled') && (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                >
                                    <g clipPath="url(#clip0_3719_49400)">
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M1.9334 8.00006C1.9334 4.64954 4.64954 1.9334 8.00006 1.9334C11.3506 1.9334 14.0667 4.64954 14.0667 8.00006C14.0667 11.3506 11.3506 14.0667 8.00006 14.0667C4.64954 14.0667 1.9334 11.3506 1.9334 8.00006ZM8.00006 0.733398C3.9868 0.733398 0.733398 3.9868 0.733398 8.00006C0.733398 12.0133 3.9868 15.2667 8.00006 15.2667C12.0133 15.2667 15.2667 12.0133 15.2667 8.00006C15.2667 3.9868 12.0133 0.733398 8.00006 0.733398ZM5.3334 7.30827C5.00203 7.30827 4.7334 7.5769 4.7334 7.90827C4.7334 8.23964 5.00203 8.50827 5.3334 8.50827H10.6667C10.9981 8.50827 11.2667 8.23964 11.2667 7.90827C11.2667 7.5769 10.9981 7.30827 10.6667 7.30827H5.3334Z"
                                            fill="#77777C"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_3719_49400">
                                            <rect width="16" height="16" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            )}
                            {state === 'optional' && (
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_2726_42457)">
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M1.93438 8.00104C1.93438 4.65051 4.65051 1.93438 8.00104 1.93438C11.3516 1.93438 14.0677 4.65051 14.0677 8.00104C14.0677 11.3516 11.3516 14.0677 8.00104 14.0677C4.65051 14.0677 1.93438 11.3516 1.93438 8.00104ZM8.00104 0.734375C3.98777 0.734375 0.734375 3.98777 0.734375 8.00104C0.734375 12.0143 3.98777 15.2677 8.00104 15.2677C12.0143 15.2677 15.2677 12.0143 15.2677 8.00104C15.2677 3.98777 12.0143 0.734375 8.00104 0.734375Z"
                                            fill="#77777C"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_2726_42457">
                                            <rect width="16" height="16" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            )}
                            {state === 'active' && (
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle cx="8" cy="8" r="6" stroke="#1D2E50" strokeWidth="4" />
                                </svg>
                            )}
                            {state === 'success' && (
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M8.00007 0.733398C3.9868 0.733398 0.733398 3.9868 0.733398 8.00007C0.733398 12.0133 3.9868 15.2667 8.00007 15.2667C12.0133 15.2667 15.2667 12.0133 15.2667 8.00007C15.2667 3.9868 12.0133 0.733398 8.00007 0.733398ZM11.0836 6.76495C11.3219 6.53473 11.3285 6.15489 11.0983 5.91655C10.8681 5.67821 10.4882 5.67162 10.2499 5.90184L7.21577 8.83254L5.75026 7.41699C5.51192 7.18677 5.13208 7.19336 4.90186 7.4317C4.67164 7.67004 4.67823 8.04989 4.91657 8.2801L6.79892 10.0983C7.03144 10.3229 7.40009 10.3229 7.63261 10.0983L11.0836 6.76495Z"
                                        fill="#006D33"
                                    />
                                </svg>
                            )}
                            {state === 'error' && (
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_1072_46751)">
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M0.733398 8.00007C0.733398 3.9868 3.9868 0.733398 8.00007 0.733398C12.0133 0.733398 15.2667 3.9868 15.2667 8.00007C15.2667 12.0133 12.0133 15.2667 8.00007 15.2667C3.9868 15.2667 0.733398 12.0133 0.733398 8.00007ZM8.00008 9.26673C7.66871 9.26673 7.40008 8.9981 7.40008 8.66673V4.66673C7.40008 4.33536 7.66871 4.06673 8.00008 4.06673C8.33145 4.06673 8.60008 4.33536 8.60008 4.66673V8.66673C8.60008 8.9981 8.33145 9.26673 8.00008 9.26673ZM8.00008 11.3334C8.36827 11.3334 8.66675 11.0349 8.66675 10.6667C8.66675 10.2985 8.36827 10.0001 8.00008 10.0001C7.63189 10.0001 7.33341 10.2985 7.33341 10.6667C7.33341 11.0349 7.63189 11.3334 8.00008 11.3334Z"
                                            fill="#B62626"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_1072_46751">
                                            <rect width="16" height="16" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            )}
                        </div>
                    </ApplySkeleton>
                    <ApplySkeleton isLoading={isLoading}>
                        <span
                            className={clsx(`${baseClass}__title`, `${baseClass}__title--${state}`, {
                                [`${className}__title`]: !!className,
                                [`${className}__title--${state}`]: !!className && state,
                                [`${baseClass}__title--loading`]: isLoading,
                                [`${className}__title--loading`]: !!className && isLoading,
                            })}
                        >
                            {name === 'cv' && state === 'parsing'
                                ? getTitle('10695', 'Reading CV') + '..'
                                : children || title}
                        </span>
                    </ApplySkeleton>
                </div>
            </button>
        </Tooltip>
    )
}

export default ApplyNavBtn
